import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiUser,
  FiTarget,
  FiFileText,
  FiUsers,
  FiLayers,
  FiActivity,
  FiAward,
  FiStar,
  FiChevronRight,
  FiEdit3,
  FiCalendar,
  FiHeart,
  FiMessageCircle,
  FiRepeat,
  FiBookOpen,
  FiHome,
  FiImage,
  FiHelpCircle,
  FiBarChart2,
  FiTrendingUp,
} from "react-icons/fi";
import "./OverviewTab.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const cardHover = {
  y: -4,
  transition: { duration: 0.2 },
};

// Section Header Component
const SectionHeader = ({ icon: Icon, title, onViewAll, count }) => (
  <div className="ovw-section-header">
    <div className="ovw-section-title">
      <Icon className="ovw-section-icon" />
      <h3>{title}</h3>
      {count > 0 && <span className="ovw-section-count">{count}</span>}
    </div>
    {onViewAll && (
      <motion.button
        className="ovw-view-all"
        onClick={onViewAll}
        whileHover={{ x: 4 }}
        whileTap={{ scale: 0.98 }}
      >
        View All <FiChevronRight />
      </motion.button>
    )}
  </div>
);

// About Card Component
const AboutCard = ({ profile, isOwnProfile }) => (
  <motion.div className="ovw-card ovw-about-card" variants={itemVariants}>
    <div className="ovw-card-header">
      <div className="ovw-card-title">
        <FiUser />
        <h3>About</h3>
      </div>
      {isOwnProfile && (
        <Link to="/settings/profile" className="ovw-edit-btn">
          <FiEdit3 />
        </Link>
      )}
    </div>
    <div className="ovw-card-body">
      <p className="ovw-about-text">
        {profile?.bio || "No bio provided yet. Tell others about yourself!"}
      </p>
      {/* Display goals - check for structured goals array OR goalsText */}
      {((Array.isArray(profile?.goals) && profile.goals.length > 0) || profile?.goalsText) && (
        <div className="ovw-goals">
          <div className="ovw-goals-header">
            <FiTarget className="ovw-goals-icon" />
            <h4>Research Goals</h4>
          </div>
          <p className="ovw-goals-text">
            {/* Priority: structured goals > goalsText > fallback */}
            {Array.isArray(profile.goals) && profile.goals.length > 0
              ? profile.goals.map(g => {
                  if (g.type === 'custom') return g.customText;
                  // Capitalize and format goal type, add custom text if present
                  const typeLabel = g.type?.charAt(0).toUpperCase() + g.type?.slice(1);
                  return g.customText ? `${typeLabel}: ${g.customText}` : typeLabel;
                }).filter(Boolean).join(' • ')
              : profile.goalsText || 'Goals not specified'}
          </p>
        </div>
      )}
    </div>
  </motion.div>
);

// Mini Post Card (for overview, links to Posts tab)
const MiniPostCard = ({ post, onClick }) => {
  const getPostTypeIcon = (type) => {
    const iconProps = { className: "ovw-post-type-icon" };
    switch (type) {
      case "photo":
        return <FiImage {...iconProps} style={{ color: "#3b82f6" }} />;
      case "research":
        return <FiFileText {...iconProps} style={{ color: "#10b981" }} />;
      case "question":
        return <FiHelpCircle {...iconProps} style={{ color: "#f59e0b" }} />;
      case "collaboration":
        return <FiUsers {...iconProps} style={{ color: "#6366f1" }} />;
      case "poll":
        return <FiBarChart2 {...iconProps} style={{ color: "#06b6d4" }} />;
      case "milestone":
        return <FiTrendingUp {...iconProps} style={{ color: "#ec4899" }} />;
      default:
        return <FiEdit3 {...iconProps} style={{ color: "#64748b" }} />;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <motion.div
      className="ovw-mini-post"
      whileHover={cardHover}
      onClick={onClick}
    >
      <div className="ovw-mini-post-header">
        <img
          src={
            post.author?.profilePhotoUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              post.author?.name || "User"
            )}&background=3b82f6&color=ffffff`
          }
          alt={post.author?.name}
          className="ovw-mini-post-avatar"
        />
        <div className="ovw-mini-post-meta">
          <span className="ovw-mini-post-author">{post.author?.name}</span>
          <span className="ovw-mini-post-date">
            {formatDate(post.createdAt)}
          </span>
        </div>
        <span className="ovw-mini-post-type">
          {getPostTypeIcon(post.postType)}
        </span>
      </div>
      <p className="ovw-mini-post-content">
        {post.content?.substring(0, 120) ||
          post.title?.substring(0, 120) ||
          "No content"}
        {(post.content?.length > 120 || post.title?.length > 120) && "..."}
      </p>
      {post.media?.length > 0 && (
        <div className="ovw-mini-post-media">
          <img src={post.media[0].url} alt="Post media" />
        </div>
      )}
      <div className="ovw-mini-post-stats">
        <span>
          <FiHeart /> {post.likeCount || 0}
        </span>
        <span>
          <FiMessageCircle /> {post.commentCount || 0}
        </span>
        <span>
          <FiRepeat /> {post.repostCount || 0}
        </span>
      </div>
    </motion.div>
  );
};

// Research Space Card - with proper photo priority & navigation
const ResearchSpaceCard = ({ room, getFieldImage }) => {
  // Photo priority: signedPhotoURL > photoURL > field-based > fallback
  const getPhotoSrc = () => {
    if (room.signedPhotoURL) return room.signedPhotoURL;
    if (room.photoURL) return room.photoURL;
    return getFieldImage(room.field);
  };

  return (
    <Link to={room.previewUrl || `/labs/${room._id || room.id}/preview`} className="ovw-space-link">
      <motion.div className="ovw-space-card" whileHover={cardHover}>
        <div className="ovw-space-image">
          <img
            src={getPhotoSrc()}
            alt={room.name || room.field}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/field-images/default.jpg";
            }}
          />
          <div className="ovw-space-overlay">
            <span className="ovw-space-field">{room.field || "Research"}</span>
            {room.userRole === 'creator' && (
              <span className="ovw-role-badge ovw-role-creator">Creator</span>
            )}
          </div>
        </div>
        <div className="ovw-space-content">
          <h4 className="ovw-space-title">{room.name || room.title}</h4>
          <p className="ovw-space-desc">
            {room.description?.substring(0, 80) || "Active research collaboration"}
            {room.description?.length > 80 && "..."}
          </p>
          <div className="ovw-space-meta">
            <span className="ovw-space-members">
              <FiUsers /> {room.memberCount || room.members?.length || 0}
            </span>
            <span className="ovw-space-date">
              <FiCalendar />{" "}
              {room.createdAt
                ? new Date(room.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    year: "numeric",
                  })
                : "Recently"}
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

// Community Card - with proper photo priority & navigation
const CommunityCard = ({ community, getCommunityImage }) => {
  // Photo priority: signedPhotoURL > photoURL > fallback
  const getPhotoSrc = () => {
    if (community.signedPhotoURL) return community.signedPhotoURL;
    if (community.photoURL) return community.photoURL;
    return getCommunityImage(community);
  };

  return (
    <Link to={community.previewUrl || `/communities/${community._id || community.id}/preview`} className="ovw-community-link">
      <motion.div className="ovw-community-card" whileHover={cardHover}>
        <img
          src={getPhotoSrc()}
          alt={community.name}
          className="ovw-community-img"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/field-images/default.jpg";
          }}
        />
        <div className="ovw-community-info">
          <h4>{community.name}</h4>
          <span className="ovw-community-members">
            <FiUsers /> {community.memberCount || community.members?.length || 0}{" "}
            members
          </span>
          {(community.userRole === 'creator' || community.userRole === 'moderator') && (
            <span className={`ovw-role-badge ovw-role-${community.userRole}`}>
              {community.userRole === 'creator' ? 'Creator' : 'Mod'}
            </span>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

// Activity Item
const ActivityItem = ({ activity }) => {
  const getActivityIcon = (type) => {
    const iconMap = {
      post_created: FiFileText,
      room_joined: FiHome,
      room_created: FiLayers,
      community_joined: FiUsers,
      workshop_created: FiBookOpen,
      skill_endorsed: FiAward,
    };
    return iconMap[type?.toLowerCase()] || FiActivity;
  };

  const getActivityColor = (type) => {
    const colorMap = {
      post_created: "var(--prf-blue)",
      room_joined: "var(--prf-green)",
      room_created: "var(--prf-purple)",
      community_joined: "var(--prf-indigo)",
      workshop_created: "var(--prf-amber)",
      skill_endorsed: "var(--prf-pink)",
    };
    return colorMap[type?.toLowerCase()] || "var(--prf-slate-500)";
  };

  const Icon = getActivityIcon(activity.type);
  const color = getActivityColor(activity.type);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="ovw-activity-item">
      <div
        className="ovw-activity-icon"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <Icon />
      </div>
      <div className="ovw-activity-content">
        <p className="ovw-activity-title">
          {activity.action ||
            activity.description ||
            activity.type?.replace(/_/g, " ")}
        </p>
        <span className="ovw-activity-subtitle">
          {activity.data?.name || activity.data?.title || "Activity"}
        </span>
      </div>
      <span className="ovw-activity-date">
        {activity.createdAt ? formatDate(activity.createdAt) : "Recently"}
      </span>
    </div>
  );
};

// Skill Badge
const SkillBadge = ({ skill }) => (
  <motion.div className="ovw-skill-badge" whileHover={{ scale: 1.02 }}>
    <span className="ovw-skill-name">{skill.skill || skill.name}</span>
    <span className="ovw-skill-count">
      {skill.endorsements?.length || skill.endorsementCount || 0} endorsements
    </span>
  </motion.div>
);

// Review Card - Compatible with backend API response format
// Backend returns: rater (not fromUser), feedback (not review), overallRating (or rating)
const ReviewCard = ({ review }) => {
  const formatDate = (date) => {
    if (!date) return "Recently";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Support both backend field names (rater) and legacy names (fromUser)
  const reviewer = review.rater || review.fromUser || {};
  // Support both overallRating and rating
  const rating = review.overallRating || review.rating || 0;
  // Support feedback (backend), review (legacy), and comment
  const reviewText =
    review.feedback ||
    review.review ||
    review.comment ||
    "Great collaboration experience!";

  return (
    <motion.div className="ovw-review-card" whileHover={cardHover}>
      <div className="ovw-review-header">
        <img
          src={
            reviewer.profilePhotoUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              reviewer.name || "User"
            )}&background=3b82f6&color=ffffff`
          }
          alt={reviewer.name}
          className="ovw-review-avatar"
        />
        <div className="ovw-review-meta">
          <span className="ovw-review-author">
            {review.isAnonymous ? "Anonymous" : reviewer.name || "Anonymous"}
          </span>
          <span className="ovw-review-date">
            {formatDate(review.createdAt)}
          </span>
        </div>
        <div className="ovw-review-rating">
          {[...Array(5)].map((_, i) => (
            <FiStar key={i} className={i < rating ? "filled" : ""} />
          ))}
        </div>
      </div>
      <p className="ovw-review-text">
        "{reviewText.substring(0, 150)}"{reviewText.length > 150 && "..."}
      </p>
    </motion.div>
  );
};

// Main Component
const OverviewTab = ({
  profile,
  posts,
  researchRooms,
  communities,
  activityFeed,
  stats,
  isOwnProfile,
  handleTabChange,
  getFieldImage,
  getCommunityImage,
  skills,
  reviews,
  reviewStats,
}) => {
  const displayPosts = posts?.slice(0, 3) || [];
  const displayRooms = researchRooms?.slice(0, 3) || [];
  const displayCommunities = communities?.slice(0, 2) || [];
  const displayActivities = activityFeed?.slice(0, 5) || [];
  const displaySkills = skills?.slice(0, 4) || [];
  const displayReviews = reviews?.slice(0, 2) || [];

  return (
    <motion.div
      className="ovw-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Main Grid Layout */}
      <div className="ovw-grid">
        {/* Left Column */}
        <div className="ovw-left-col">
          {/* About Section */}
          <AboutCard profile={profile} isOwnProfile={isOwnProfile} />

          {/* Latest Posts */}
          <motion.div className="ovw-card" variants={itemVariants}>
            <SectionHeader
              icon={FiFileText}
              title="Latest Posts"
              count={stats?.posts}
              onViewAll={() => handleTabChange("posts")}
            />
            <div className="ovw-posts-list">
              {displayPosts.length > 0 ? (
                displayPosts.map((post) => (
                  <MiniPostCard
                    key={post._id || post.id}
                    post={post}
                    onClick={() => handleTabChange("posts")}
                  />
                ))
              ) : (
                <div className="ovw-empty">
                  <FiFileText className="ovw-empty-icon" />
                  <p>No posts yet</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Research Spaces */}
          <motion.div className="ovw-card" variants={itemVariants}>
            <SectionHeader
              icon={FiLayers}
              title="Active Research Spaces"
              count={displayRooms.length}
              onViewAll={() => handleTabChange("research")}
            />
            <div className="ovw-spaces-grid">
              {displayRooms.length > 0 ? (
                displayRooms.map((room) => (
                  <ResearchSpaceCard
                    key={room._id || room.id}
                    room={room}
                    getFieldImage={getFieldImage}
                  />
                ))
              ) : (
                <div className="ovw-empty">
                  <FiLayers className="ovw-empty-icon" />
                  <p>No research spaces</p>
                </div>
              )}
            </div>

            {/* Communities Row */}
            {displayCommunities.length > 0 && (
              <div className="ovw-communities-row">
                {displayCommunities.map((community) => (
                  <CommunityCard
                    key={community._id || community.id}
                    community={community}
                    getCommunityImage={getCommunityImage}
                  />
                ))}
              </div>
            )}
          </motion.div>

        </div>

        {/* Right Column */}
        <div className="ovw-right-col">
          {/* Recent Activity */}
          <motion.div className="ovw-card" variants={itemVariants}>
            <SectionHeader
              icon={FiActivity}
              title="Recent Activity"
              onViewAll={() => handleTabChange("activity")}
            />
            <div className="ovw-activity-list">
              {displayActivities.length > 0 ? (
                displayActivities.map((activity, index) => (
                  <ActivityItem
                    key={activity._id || index}
                    activity={activity}
                  />
                ))
              ) : (
                <div className="ovw-empty">
                  <FiActivity className="ovw-empty-icon" />
                  <p>No recent activity</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Top Skills */}
          <motion.div className="ovw-card" variants={itemVariants}>
            <SectionHeader
              icon={FiAward}
              title="Top Skills"
              onViewAll={() => handleTabChange("endorsements")}
            />
            <div className="ovw-skills-list">
              {displaySkills.length > 0 ? (
                displaySkills.map((skill, index) => (
                  <SkillBadge key={skill._id || index} skill={skill} />
                ))
              ) : (
                <div className="ovw-empty">
                  <FiAward className="ovw-empty-icon" />
                  <p>No skills added</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Reviews & Ratings */}
          <motion.div className="ovw-card" variants={itemVariants}>
            <SectionHeader
              icon={FiStar}
              title="Reviews & Ratings"
              onViewAll={() => handleTabChange("reviews")}
            />
            <div className="ovw-reviews-list">
              {displayReviews.length > 0 ? (
                displayReviews.map((review, index) => (
                  <ReviewCard key={review._id || index} review={review} />
                ))
              ) : (
                <div className="ovw-empty">
                  <FiStar className="ovw-empty-icon" />
                  <p>No reviews yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default OverviewTab;
